import { useEffect, useState } from "react";
import Map from "@components/Mapa/Map";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import { HollowedButton, NormalButton } from "@shared/designSystem/Button";
import { ColumnsContainer, HorizontalContainer, SimpleCardContainer, VerticalContainer } from "@shared/designSystem/Containers";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import DetailField from "@shared/designSystem/DetailField";
import { Modal } from "@shared/designSystem/messages/Modal";
import { Text } from "@shared/designSystem/Texts";
import { TextAreaInput } from "@shared/designSystem/TextInput";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { PresenteeismConciliationDetailService } from "@services/PresenteeismConciliationDetailService";
import { AnalyticsHelper } from "@core/Analytics/AnalyticsHelper";
import {
    PresenteeismConciliationDetailRowRequest,
    usePresenteeismConciliationDetailModalViewModel,
} from "./PresenteeismConciliationDetailModalViewModel";

interface PresenteeismConciliationDetailModalProps {
    request: PresenteeismConciliationDetailRowRequest | null;
    isOpen: boolean;
    onClose: () => void;
    onActionCompleted?: () => void;
    service: PresenteeismConciliationDetailService;
    timezone: string;
}

interface ModalDetailSection {
    title: string;
    fields: Array<{
        label: string;
        value?: string | number | null;
        url?: string | null;
    }>;
    mapData?: {
        markers: Array<{
            id: string;
            status: string;
            latitude: number;
            longitude: number;
            title: string;
        }>;
        circles: Array<{
            id: string;
            center: {
                latitude: number;
                longitude: number;
            };
            radiusInMeters: number;
            strokeColor: string;
            fillColor: string;
        }>;
    };
}

const DetailSection: React.FC<{ section: ModalDetailSection }> = ({ section }) => (
    <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
        <Text text={`**${section.title.trim()}**`} color={designSystemStyles.colorText} markdown={true} />
        <VerticalContainer
            width="100%"
            gap={designSystemStyles.gapMd}
            backgroundColor={designSystemStyles.colorBackgroundGray2}
            borderRadius={designSystemStyles.cornerRadiusComponent}
            padding={designSystemStyles.paddingMd}
        >
            <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                {section.fields.map((field) => {
                    const detailField = (
                        <DetailField
                            key={`${section.title}-${field.label}`}
                            label={field.label}
                            value={field.value}
                        />
                    );

                    return field.url ? (
                        <ClickableWrapper
                            key={`${section.title}-${field.label}`}
                            onClick={() => window.open(field.url, "_blank", "noopener,noreferrer")}
                        >
                            {detailField}
                        </ClickableWrapper>
                    ) : detailField;
                })}
            </ColumnsContainer>
            {section.mapData ? (
                <div style={{ width: "100%", height: "320px" }}>
                    <Map
                        markers={section.mapData.markers}
                        circles={section.mapData.circles}
                        radius={designSystemStyles.cornerRadiusComponent}
                        setMapRefParent={() => {}}
                    />
                </div>
            ) : null}
        </VerticalContainer>
    </VerticalContainer>
);

const PresenteeismConciliationDetailModal: React.FC<PresenteeismConciliationDetailModalProps> = ({
    request,
    isOpen,
    onClose,
    onActionCompleted,
    service,
    timezone,
}) => {
    const [isCancelationSetupVisible, setIsCancelationSetupVisible] = useState(false);
    const [isUndoSetupVisible, setIsUndoSetupVisible] = useState(false);
    const viewModel = usePresenteeismConciliationDetailModalViewModel({
        isOpen,
        request,
        service,
        timezone,
    });

    useEffect(() => {
        setIsCancelationSetupVisible(false);
        setIsUndoSetupVisible(false);
    }, [isOpen, request]);

    useEffect(() => {
        if (!viewModel.canCancel) {
            setIsCancelationSetupVisible(false);
        }
    }, [viewModel.canCancel]);

    useEffect(() => {
        if (!viewModel.canUndoCancelation) {
            setIsUndoSetupVisible(false);
        }
    }, [viewModel.canUndoCancelation]);

    const handleDismissCancelationSetup = () => {
        viewModel.resetCancelationDraft();
        setIsCancelationSetupVisible(false);
    };

    const handleDismissUndoSetup = () => {
        viewModel.resetUndoCancelationDraft();
        setIsUndoSetupVisible(false);
    };

    const handleCancelConciliation = async () => {
        const detailBeforeCancelation = viewModel.detail;
        const reasonLength = viewModel.cancelReason.trim().length;
        const canceled = await viewModel.cancelConciliation();
        if (canceled) {
            AnalyticsHelper.trackPresenteeismConciliationCanceled({
                organizationID: detailBeforeCancelation?.organizationID ?? request?.organizationID,
                requesterID: detailBeforeCancelation?.requesterID ?? request?.requesterID,
                tableRecordKey: detailBeforeCancelation?.tableRecordKey ?? request?.tableRecordKey,
                day: detailBeforeCancelation?.checkinRecord?.day ?? request?.day,
                buildingID: detailBeforeCancelation?.buildingID ?? request?.buildingID,
                checkinRecordID: detailBeforeCancelation?.checkinRecordID,
                checkoutRecordID: detailBeforeCancelation?.checkoutRecordID,
                checkInTimestamp: detailBeforeCancelation?.checkinRecord?.timestamp ?? request?.checkInTimestamp,
                reasonLength,
            });
            setIsCancelationSetupVisible(false);
            onActionCompleted?.();
        }
    };

    const handleUndoCancelation = async () => {
        const detailBeforeUndo = viewModel.detail;
        const restored = await viewModel.undoCancelation();
        if (restored) {
            AnalyticsHelper.trackPresenteeismConciliationUndo({
                organizationID: detailBeforeUndo?.organizationID ?? request?.organizationID,
                requesterID: detailBeforeUndo?.requesterID ?? request?.requesterID,
                tableRecordKey: detailBeforeUndo?.tableRecordKey ?? request?.tableRecordKey,
                day: detailBeforeUndo?.checkinRecord?.day ?? request?.day,
                buildingID: detailBeforeUndo?.buildingID ?? request?.buildingID,
                checkinRecordID: detailBeforeUndo?.checkinRecordID,
                checkoutRecordID: detailBeforeUndo?.checkoutRecordID,
                checkInTimestamp: detailBeforeUndo?.checkinRecord?.timestamp ?? request?.checkInTimestamp,
                cancellationUndoID: detailBeforeUndo?.cancellationUndoID,
            });
            setIsUndoSetupVisible(false);
            onActionCompleted?.();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={viewModel.title}
            width="880px"
            height="75vh"
            maxWidth="94vw"
        >
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
                <div style={{ width: "100%", flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {viewModel.loading ? (
                        <QontyLoading
                            text={"Cargando detalle de conciliación".localized()}
                            withQonty={true}
                        />
                    ) : viewModel.error ? (
                        <QontyEmptyState text={viewModel.error} />
                    ) : viewModel.detail ? (
                        <VerticalContainer
                            width="100%"
                            gap={designSystemStyles.gapLg}
                            paddingBottom={designSystemStyles.paddingSm}
                        >
                            {viewModel.sections.map((section) => (
                                <DetailSection key={section.title} section={section} />
                            ))}
                        </VerticalContainer>
                    ) : (
                        <QontyEmptyState
                            text={"No hay detalle de conciliación para mostrar.".localized()}
                        />
                    )}
                </div>

                <VerticalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapSm}>
                    {!isCancelationSetupVisible && !isUndoSetupVisible ? (
                        <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapMd}>
                            {viewModel.canUndoCancelation ? (
                                <HollowedButton
                                    text={"Restaurar conciliación".localized()}
                                    onClick={() => setIsUndoSetupVisible(true)}
                                    disabled={viewModel.undoingCancelation}
                                    loading={viewModel.undoingCancelation}
                                    width="220px"
                                />
                            ) : null}
                            {viewModel.canCancel ? (
                                <HollowedButton
                                    text={"Cancelar conciliación".localized()}
                                    onClick={() => {
                                        setIsUndoSetupVisible(false);
                                        setIsCancelationSetupVisible(true);
                                    }}
                                    disabled={viewModel.canceling}
                                    width="220px"
                                />
                            ) : null}
                            <HollowedButton
                                text={"Cerrar".localized()}
                                onClick={onClose}
                            />
                        </HorizontalContainer>
                    ) : isCancelationSetupVisible ? (
                        <SimpleCardContainer width="100%" variant="destructiveInlineSetup">
                            <VerticalContainer width="100%" gap={designSystemStyles.gapSm} alignment="start">
                                <Text
                                    text={"**"+"Indica el motivo de cancelación de la conciliación.".localized()+"**"}
                                    color={designSystemStyles.colorTitleText}
                                    markdown={true}
                                />
                                <TextAreaInput
                                    label={"Motivo de cancelación".localized()}
                                    value={viewModel.cancelReason}
                                    onChange={viewModel.setCancelReason}
                                    placeholder={"Ingresá el motivo".localized()}
                                    error={viewModel.cancelError}
                                    loading={viewModel.canceling}
                                />
                                <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapSm}>
                                    <HollowedButton
                                        text={"Cancelar".localized()}
                                        onClick={handleDismissCancelationSetup}
                                        disabled={viewModel.canceling}
                                        width="180px"
                                    />
                                    <NormalButton
                                        text={"Enviar".localized()}
                                        onClick={handleCancelConciliation}
                                        disabled={!viewModel.canSubmitCancelation}
                                        loading={viewModel.canceling}
                                        width="180px"
                                    />
                                </HorizontalContainer>
                            </VerticalContainer>
                        </SimpleCardContainer>
                    ) : (
                        <SimpleCardContainer width="100%" variant="inlineSetup">
                            <VerticalContainer width="100%" gap={designSystemStyles.gapSm} alignment="start">
                                <Text
                                    text={"**"+"¿Querés restaurar esta conciliación cancelada?".localized()+"**"}
                                    color={designSystemStyles.colorTitleText}
                                    markdown={true}
                                />
                                <Text
                                    text={"Se restaurarán los cálculos originales de entrada y salida.".localized()}
                                    color={designSystemStyles.colorText}
                                />
                                {viewModel.undoCancelationError ? (
                                    <Text
                                        text={viewModel.undoCancelationError}
                                        color={designSystemStyles.colorError}
                                    />
                                ) : null}
                                <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapSm}>
                                    <HollowedButton
                                        text={"Cancelar".localized()}
                                        onClick={handleDismissUndoSetup}
                                        disabled={viewModel.undoingCancelation}
                                        width="180px"
                                    />
                                    <NormalButton
                                        text={"Restaurar".localized()}
                                        onClick={handleUndoCancelation}
                                        disabled={!viewModel.canSubmitUndoCancelation}
                                        loading={viewModel.undoingCancelation}
                                        width="180px"
                                    />
                                </HorizontalContainer>
                            </VerticalContainer>
                        </SimpleCardContainer>
                    )}
                </VerticalContainer>
            </VerticalContainer>
        </Modal>
    );
};

export default PresenteeismConciliationDetailModal;
